/**
 * Dashboard Module Registry
 * Defines modules available for each dashboard tier and their permission requirements.
 * Used for dynamic dashboard rendering based on user's dashboard_tier and permissions.
 */

// Dashboard tiers
export const DASHBOARD_TIERS = {
  ADMIN: 'admin',
  CHECKER: 'checker',
  MAKER: 'maker',
  USER: 'user',
};

// Module categories
export const MODULE_CATEGORIES = {
  SELF_SERVICE: 'Self Service',
  LEAVE_MANAGEMENT: 'Leave Management',
  ATTENDANCE: 'Attendance',
  PAYROLL: 'Payroll',
  //LOANS: 'Loans & Advances',
  PF: 'PF Management',
  TAX: 'Income Tax',
  REIMBURSEMENT: 'Reimbursement',
  ADMINISTRATION: 'Administration',
  REPORTS: 'Reports',
};

// Module registry
export const DASHBOARD_MODULES = {
  // Self Service
  profile: {
    key: 'profile',
    label: 'My Profile',
    icon: 'User',
    category: MODULE_CATEGORIES.SELF_SERVICE,
    route: '/employee/profile',
    description: 'View and update personal information',
    requiredPermission: 'employee:update:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 1,
  },

  // Attendance
  attendance: {
    key: 'attendance',
    label: 'My Attendance',
    icon: 'Clock',
    category: MODULE_CATEGORIES.ATTENDANCE,
    route: '/employee/attendance',
    description: 'View attendance records and mark attendance',
    requiredPermission: 'attendance:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 2,
  },

  // Leave Management
  leaves: {
    key: 'leaves',
    label: 'My Leaves',
    icon: 'Calendar',
    category: MODULE_CATEGORIES.LEAVE_MANAGEMENT,
    route: '/employee/leaves',
    description: 'Apply for leaves and view leave balance',
    requiredPermission: 'leave:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 3,
  },

  // Payroll
  salary_slips: {
    key: 'salary_slips',
    label: 'My Salary Slips',
    icon: 'IndianRupee',
    category: MODULE_CATEGORIES.PAYROLL,
    route: '/employee/salary-slips',
    description: 'View salary slips and payroll information',
    requiredPermission: 'payroll:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 4,
  },

  // Loans
  loans: {
    key: 'loans',
    label: 'My Loans',
    icon: 'CreditCard',
    category: MODULE_CATEGORIES.LOANS,
    route: '/employee/loans',
    description: 'View loan applications and status',
    requiredPermission: 'loan:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 5,
  },

  advances: {
    key: 'advances',
    label: 'My Advances',
    icon: 'TrendingUp',
    category: MODULE_CATEGORIES.LOANS,
    route: '/employee/advances',
    description: 'View advance applications and status',
    requiredPermission: 'advance:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 6,
  },

  festival_advance: {
    key: 'festival_advance',
    label: 'Festival Advance',
    icon: 'Plane',
    category: MODULE_CATEGORIES.LOANS,
    route: '/employee/festival-advance',
    description: 'Apply for festival advance',
    requiredPermission: 'festival_advance:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 7,
  },

  // PF Management
  pf: {
    key: 'pf',
    label: 'My PF',
    icon: 'Shield',
    category: MODULE_CATEGORIES.PF,
    route: '/employee/pf',
    description: 'View PF account and balance',
    requiredPermission: 'pf:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 8,
  },

  // Bank Loans
  bank_loans: {
    key: 'bank_loans',
    label: 'Bank Loans',
    icon: 'Building2',
    category: MODULE_CATEGORIES.LOANS,
    route: '/employee/bank-loans',
    description: 'View bank loan information',
    requiredPermission: 'bank_loan:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 9,
  },

  // LIC
  lic: {
    key: 'lic',
    label: 'My LIC',
    icon: 'FileText',
    category: MODULE_CATEGORIES.PAYROLL,
    route: '/employee/lic',
    description: 'View LIC policies',
    requiredPermission: 'lic:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 10,
  },

  // Reimbursement
  reimbursement: {
    key: 'reimbursement',
    label: 'Reimbursement',
    icon: 'RefreshCw',
    category: MODULE_CATEGORIES.REIMBURSEMENT,
    route: '/employee/reimbursement',
    description: 'Submit and view reimbursement claims',
    requiredPermission: 'reimbursement:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 11,
  },

  // Income Tax
  income_tax: {
    key: 'income_tax',
    label: 'Income Tax',
    icon: 'Target',
    category: MODULE_CATEGORIES.TAX,
    route: '/employee/income-tax',
    description: 'View income tax details',
    requiredPermission: 'employee:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 12,
  },

  // Increment
  increment: {
    key: 'increment',
    label: 'My Increment',
    icon: 'TrendingUp',
    category: MODULE_CATEGORIES.PAYROLL,
    route: '/employee/increment',
    description: 'View increment history',
    requiredPermission: 'increment:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 13,
  },

  // Forms
  forms: {
    key: 'forms',
    label: 'My Forms',
    icon: 'StickyNoteIcon',
    category: MODULE_CATEGORIES.SELF_SERVICE,
    route: '/employee/forms',
    description: 'View and submit forms',
    requiredPermission: 'employee:view:own',
    allowedTiers: [DASHBOARD_TIERS.USER, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER, DASHBOARD_TIERS.ADMIN],
    order: 14,
  },

  // Admin-only modules
  employee_management: {
    key: 'employee_management',
    label: 'Employee Management',
    icon: 'Users',
    category: MODULE_CATEGORIES.ADMINISTRATION,
    route: '/admin/employees',
    description: 'Manage employee records',
    requiredPermission: 'employee:view:all',
    allowedTiers: [DASHBOARD_TIERS.ADMIN, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER],
    order: 20,
  },

  role_management: {
    key: 'role_management',
    label: 'Role Management',
    icon: 'Shield',
    category: MODULE_CATEGORIES.ADMINISTRATION,
    route: '/admin/roles',
    description: 'Manage roles and permissions',
    requiredPermission: 'role:view',
    allowedTiers: [DASHBOARD_TIERS.ADMIN],
    order: 21,
  },

  attendance_management: {
    key: 'attendance_management',
    label: 'Attendance Management',
    icon: 'Clock',
    category: MODULE_CATEGORIES.ATTENDANCE,
    route: '/admin/attendance',
    description: 'Manage attendance records',
    requiredPermission: 'attendance:view:all',
    allowedTiers: [DASHBOARD_TIERS.ADMIN, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER],
    order: 22,
  },

  leave_management: {
    key: 'leave_management',
    label: 'Leave Management',
    icon: 'Calendar',
    category: MODULE_CATEGORIES.LEAVE_MANAGEMENT,
    route: '/admin/leaves',
    description: 'Manage leave requests',
    requiredPermission: 'leave:view:all',
    allowedTiers: [DASHBOARD_TIERS.ADMIN, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER],
    order: 23,
  },

  payroll_management: {
    key: 'payroll_management',
    label: 'Payroll Management',
    icon: 'IndianRupee',
    category: MODULE_CATEGORIES.PAYROLL,
    route: '/admin/payroll',
    description: 'Process and manage payroll',
    requiredPermission: 'payroll:view:all',
    allowedTiers: [DASHBOARD_TIERS.ADMIN, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER],
    order: 24,
  },

  reports: {
    key: 'reports',
    label: 'Reports',
    icon: 'FileText',
    category: MODULE_CATEGORIES.REPORTS,
    route: '/admin/reports',
    description: 'View and generate reports',
    requiredPermission: ['report:view:all', 'report:view:dept'],
    allowedTiers: [DASHBOARD_TIERS.ADMIN, DASHBOARD_TIERS.MAKER, DASHBOARD_TIERS.CHECKER],
    order: 25,
  },
};

/**
 * Get modules available for a specific dashboard tier and user permissions
 * @param {string} dashboardTier - User's dashboard tier (admin, checker, maker, user)
 * @param {Array<string>} permissions - User's permissions array
 * @param {boolean} isAdmin - Whether user has admin access (wildcard * permission)
 * @returns {Array} Filtered and sorted modules
 */
export const getAvailableModules = (dashboardTier, permissions = [], isAdmin = false) => {
  return Object.values(DASHBOARD_MODULES)
    .filter(module => {
      // Check if module is allowed for this dashboard tier
      if (!module.allowedTiers.includes(dashboardTier)) {
        return false;
      }

      // Admin users see all modules for their tier
      if (isAdmin) {
        return true;
      }

      // Check if user has required permission
      if (module.requiredPermission) {
        return hasPermission(permissions, module.requiredPermission);
      }

      return true;
    })
    .sort((a, b) => a.order - b.order);
};

/**
 * Check if user has a specific permission (supports wildcards)
 * @param {Array<string>} permissions - User's permissions
 * @param {string} requiredPermission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (permissions, requiredPermission) => {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  // Check for wildcard
  if (permissions.includes('*')) {
    return true;
  }

  // Check exact match
  if (permissions.includes(requiredPermission)) {
    return true;
  }

  // Check resource wildcard (e.g., "employee:*" matches "employee:view:own")
  const [resource] = requiredPermission.split(':');
  if (permissions.includes(`${resource}:*`)) {
    return true;
  }

  // Check level hierarchy for view permissions
  const parts = requiredPermission.split(':');
  if (parts.length === 3 && parts[1] === 'view') {
    const [, action, level] = parts;
    // If user has "all" level, they can access "dept" and "own" levels
    if (permissions.includes(`${resource}:${action}:all`)) {
      return true;
    }
    // If user has "dept" level and required is "own"
    if (level === 'own' && permissions.includes(`${resource}:${action}:dept`)) {
      return true;
    }
  }

  return false;
};

/**
 * Get modules grouped by category
 * @param {string} dashboardTier - User's dashboard tier
 * @param {Array<string>} permissions - User's permissions
 * @param {boolean} isAdmin - Whether user has admin access
 * @returns {Object} Modules grouped by category
 */
export const getModulesByCategory = (dashboardTier, permissions, isAdmin) => {
  const modules = getAvailableModules(dashboardTier, permissions, isAdmin);
  const grouped = {};

  modules.forEach(module => {
    if (!grouped[module.category]) {
      grouped[module.category] = [];
    }
    grouped[module.category].push(module);
  });

  return grouped;
};

export default DASHBOARD_MODULES;
